import { useTranslation } from 'react-i18next'
import styles from './PlayerAccountTable.module.css'
import { useCallback, useMemo, useState } from 'react'
import { Table, TableRow } from '../../../reports/components/GenericTable'
import { getPlayerAccountTableRow } from './getPlayerAccountRow'
import {
  BusinessPlayerAccountParams,
  BussinessPlayerAccount,
  BussinessPlayerAccounts,
  PlayerAccountInviteStatus,
  Tag,
} from '../../../../types/commonTypes'
import { SmallModal } from '../../../../common/components/Modal/SmallModal'
import { TagPill } from '../../../../common/components/TagPill/TagPill'
import { useUser } from '../../../../contexts/userContext'
import { ReportSortDir } from '../../../../contexts/ReportsContextProvider'
import { useSortedTable } from '../../../../hooks/useSortedTable'
import { PaginationSelectOption } from '../../../../composites/GamesOverview/components/PaginationControls'

type rowData = {
  rows: TableRow[]
  customClassRows: number[]
}

type PlayerAccountTableProps = {
  businessPlayerAccounts: BussinessPlayerAccounts | undefined
  onRenewInvite: (playerEmail: string) => void
  deletePlayerAccount: (playerId: number) => void
  getBusinessPlayerAccountInfo: (data?: BusinessPlayerAccountParams) => Promise<boolean>
  managePlayerAccountTags: (isRemoving: boolean, playerId: number | null, tag: Tag) => void
  allPageOptions: PaginationSelectOption[]
  hasLicences: boolean
}

export const PlayerAccountTable: React.FC<PlayerAccountTableProps> = ({
  businessPlayerAccounts,
  onRenewInvite,
  deletePlayerAccount,
  getBusinessPlayerAccountInfo,
  managePlayerAccountTags,
  allPageOptions,
  hasLicences,
}) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const [showLabelModal, setShowLabelModal] = useState(false)
  const [playerAccountInLabelModal, setPlayerAccountInLabelModal] = useState<number | null>(null)
  const [pageSizeShowAll, setPageSizeShowAll] = useState(false)

  const headers = [
    t('settings_organization.player_accounts.table_headers.name', 'Name'),
    t('settings_organization.player_accounts.table_headers.email', 'Email'),
    t('settings_organization.player_accounts.table_headers.tag', 'Tag'),
    t('settings_organization.player_accounts.table_headers.status', 'Status'),
    t('settings_organization.player_accounts.table_headers.actions', 'Actions'),
  ]

  const {
    sortedItems: sortedPlayers,
    sortState,
    setSort,
  } = useSortedTable<BussinessPlayerAccount>({
    items: businessPlayerAccounts?.players || [],
    initialSort: { sortBy: 'inviteStatus', direction: ReportSortDir.ASC },
    getSortableValue: (player, key) => {
      if (key === 'inviteStatus') {
        const value = player[key] as PlayerAccountInviteStatus
        if (value === PlayerAccountInviteStatus.EXPIRED) return 1
        if (value === PlayerAccountInviteStatus.PENDING) return 2
        return 3
      } else if (key === 'tags') {
        const value = player[key] as Tag[]
        return value[0]?.label?.trim().toLocaleLowerCase() || ''
      } else {
        const value = player[key]

        if (typeof value === 'string') return value.trim().toLocaleLowerCase()
        if (typeof value === 'number' || typeof value === 'boolean') return value

        // Fallback for undefined/null or unexpected types
        return ''
      }
    },
  })

  const handleSetShowLabelModal = useCallback((showLabel: boolean, playerAccount: BussinessPlayerAccount) => {
    setPlayerAccountInLabelModal(playerAccount.id)
    setShowLabelModal(showLabel)
  }, [])

  const tableRowData = useMemo((): rowData => {
    if (sortedPlayers && businessPlayerAccounts) {
      const newAccounts: BussinessPlayerAccounts = {
        players: sortedPlayers,
        totalPages: businessPlayerAccounts?.totalPages,
        totalPlayers: businessPlayerAccounts?.totalPlayers,
        currentPage: businessPlayerAccounts.currentPage,
      }
      return getPlayerAccountTableRow({
        businessPlayerAccounts: newAccounts,
        setShowLabelModal: handleSetShowLabelModal,
        onRenewInvite,
        onDeletePlayerAccount: deletePlayerAccount,
        t,
        hasLicences: hasLicences,
      })
    }
    const rows: TableRow[] = []
    const customClassRows: number[] = []
    return { rows, customClassRows }
  }, [
    businessPlayerAccounts,
    deletePlayerAccount,
    handleSetShowLabelModal,
    hasLicences,
    onRenewInvite,
    sortedPlayers,
    t,
  ])

  const tagsToAdd = () => {
    const returnable = user?.tags.filter((tag) => {
      return !businessPlayerAccounts?.players
        .find((account) => account.id === playerAccountInLabelModal)
        ?.tags.find((t) => tag.id === t.id)
    })
    return returnable
  }

  const hasPlayers = businessPlayerAccounts?.players?.length ? businessPlayerAccounts?.players?.length > 0 : false

  return (
    <>
      {!hasPlayers && (
        <div className={styles.noPlayersContainer}>
          {t('settings_organization.player_accounts.no_accounts', 'No player accounts')}
        </div>
      )}
      {hasPlayers && (
        <div className={styles.tableContainer}>
          <Table
            hasPagination
            currentPage={businessPlayerAccounts?.currentPage}
            pageSize={pageSizeShowAll ? 1000 : 30}
            lastPage={businessPlayerAccounts?.totalPages}
            allPageOptions={allPageOptions}
            onChangeCurrentPage={(option) => getBusinessPlayerAccountInfo({ page: option?.value })}
            onChangePageSize={(option) => {
              setPageSizeShowAll(option?.value === 1000 ? true : false)
              getBusinessPlayerAccountInfo({ showAll: pageSizeShowAll })
            }}
            onClickNext={() => getBusinessPlayerAccountInfo({ page: (businessPlayerAccounts?.currentPage || 0) + 1 })}
            onClickPrev={() => getBusinessPlayerAccountInfo({ page: (businessPlayerAccounts?.currentPage || 2) - 1 })}
            headerWidths={['30%', '70%', 'fit-content', 'fit-content', 'fit-content']}
            headerLabels={headers}
            rows={tableRowData.rows}
            pageSizeOptions={[
              { value: 30, label: '30' },
              { value: 1000, label: 'All' },
            ]}
            customClassName={styles.playerAccountTable}
            customClassRows={tableRowData.customClassRows}
            customClassRowClass={styles.cellExpired}
            removeHorizontalMargins={false}
            itemsPerPageLabel={t('settings_organization.player_accounts.per_page', 'players per page')}
            isSortable
            headerSortTags={['nickName', 'email', 'tags', 'inviteStatus']}
            onSort={(sortKey: string) => (direction: string) => {
              setSort(sortKey as keyof BussinessPlayerAccount, direction as ReportSortDir)
            }}
            activeSortCol={sortState.sortBy}
            noSortIndexes={[false, false, true, false, true]}
          />
        </div>
      )}
      {showLabelModal && (
        <SmallModal
          onClose={() => setShowLabelModal(false)}
          headerText={t('settings_organization.player_accounts.tag_modal.header', 'Tags for player account')}
          iconName='tag'
          hideCancelButton={true}
          sendButtonText={t('common.close', 'Close')}
          onSubmit={() => setShowLabelModal(false)}
        >
          <h3>
            {businessPlayerAccounts?.players.find((account) => account.id === playerAccountInLabelModal)?.nickName ||
              businessPlayerAccounts?.players.find((account) => account.id === playerAccountInLabelModal)?.email}
            :
          </h3>
          <div className={styles.tagPillContainer}>
            {businessPlayerAccounts?.players
              .find((account) => account.id === playerAccountInLabelModal)
              ?.tags.map((tag) => (
                <TagPill
                  size='small'
                  key={tag.id}
                  label={tag.label}
                  onDelete={() => managePlayerAccountTags(true, playerAccountInLabelModal || null, tag)}
                />
              ))}
          </div>
          <div className={styles.tagsContainer}>
            <h3>{t('settings_organization.player_accounts.tag_modal.all_tags_header', 'All tags')}</h3>
            <p style={{ paddingTop: '8px', paddingBottom: '8px' }}>
              {t('settings_organization.player_accounts.tag_modal.all_tags_content', 'Select to add a tag to the user')}
            </p>
            <div className={styles.allTagsContainer}>
              {tagsToAdd()?.map((tag) => (
                <TagPill
                  size='small'
                  key={tag.id}
                  label={tag.label}
                  variant='secondary'
                  onClick={() => managePlayerAccountTags(false, playerAccountInLabelModal || null, tag)}
                />
              ))}
            </div>
          </div>
        </SmallModal>
      )}
    </>
  )
}
